import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';
import {
  Toast,
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from '@/components/ui/toast';
import { useNotificationStore, Notification } from '@/store/notification.store';

interface ToastItemProps {
  notification: Notification;
  onRemove: () => void;
}

/**
 * Normalize details to an array
 */
function normalizeDetails(details: string | string[] | undefined): string[] {
  if (!details) return [];
  
  if (Array.isArray(details)) {
    return details.map(s => s.trim()).filter(Boolean);
  }
  
  // String: split by comma if it contains commas
  if (details.includes(',')) {
    return details.split(',').map(s => s.trim()).filter(Boolean);
  }
  
  // Single string without commas
  return [details.trim()].filter(Boolean);
}

async function copyTextToClipboard(text: string): Promise<void> {
  // Prefer async clipboard API when available
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  // Fallback for older browsers / insecure contexts
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();

  const success = document.execCommand('copy');
  document.body.removeChild(textarea);

  if (!success) {
    throw new Error('Copy failed');
  }
}

function buildNotificationCopyText(notification: Notification, detailItems: string[]): string {
  const title = notification.type.charAt(0).toUpperCase() + notification.type.slice(1);
  const lines: string[] = [`${title}: ${notification.message}`];

  if (detailItems.length > 0) {
    lines.push('Details:');
    lines.push(...detailItems);
  }

  return lines.join('\n');
}

function ToastItem({ notification, onRemove }: ToastItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [didCopy, setDidCopy] = useState(false);
  
  // Normalize details to array
  const detailItems = useMemo(() => normalizeDetails(notification.details), [notification.details]);
  const hasDetails = detailItems.length > 0;
  const showAsList = detailItems.length > 1;
  const copyText = useMemo(
    () => buildNotificationCopyText(notification, detailItems),
    [notification, detailItems]
  );

  return (
    <Toast
      duration={hasDetails ? Infinity : (notification.duration ?? 5000)}
      onOpenChange={(open) => {
        if (!open) {
          onRemove();
        }
      }}
      variant={
        notification.type === 'error'
          ? 'destructive'
          : notification.type === 'warning'
          ? 'warning'
          : 'default'
      }
    >
      <div className="grid gap-1 w-full">
        <ToastTitle>
          {notification.type.charAt(0).toUpperCase() + notification.type.slice(1)}
        </ToastTitle>
        <ToastDescription>{notification.message}</ToastDescription>

        <div className="mt-2 flex items-center gap-2">
          <ToastAction
            altText="Copy notification"
            onClick={async () => {
              try {
                await copyTextToClipboard(copyText);
                setDidCopy(true);
                window.setTimeout(() => setDidCopy(false), 1500);
              } catch {
                // If copy fails, do nothing; user can still manually select text.
              }
            }}
            className="h-7 px-2 text-xs cursor-pointer"
          >
            {didCopy ? <Check className="h-3.5 w-3.5 mr-1" /> : <Copy className="h-3.5 w-3.5 mr-1" />}
          </ToastAction>
        </div>
        
        {hasDetails && (
          <>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-1 text-xs font-medium mt-1 opacity-80 hover:opacity-100 transition-opacity"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="h-3 w-3" />
                  Hide details
                </>
              ) : (
                <>
                  <ChevronDown className="h-3 w-3" />
                  See details
                </>
              )}
            </button>
            
            {isExpanded && (
              <div className="mt-2 p-2 rounded bg-black/10 text-xs max-h-48 overflow-y-auto">
                {showAsList ? (
                  <ul className="list-disc list-inside space-y-1">
                    {detailItems.map((item, index) => (
                      <li key={index} className="break-words">
                        {item}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="whitespace-pre-wrap break-words">
                    {detailItems[0]}
                  </span>
                )}
              </div>
            )}
          </>
        )}
      </div>
      <ToastClose />
    </Toast>
  );
}

export function Toaster() {
  const notifications = useNotificationStore((state) => state.notifications);
  const removeNotification = useNotificationStore((state) => state.removeNotification);

  return (
    <ToastProvider>
      {notifications.map((notification) => (
        <ToastItem
          key={notification.id}
          notification={notification}
          onRemove={() => removeNotification(notification.id)}
        />
      ))}
      <ToastViewport />
    </ToastProvider>
  );
}

